/**
 * Derive firstName / lastName for createApplicant calls.
 *
 * After structured-name-identity Phase B, `CreateApplicantInput` requires
 * `firstName` and `lastName` (both non-empty). The AuthUser from AuthContext
 * now carries those fields on most sessions (they're parsed out of the
 * Cognito id_token's `given_name` / `family_name` claims). For legacy
 * sessions that pre-date Phase B, we fall back to splitting `fullName` on
 * the first whitespace.
 *
 * This helper keeps the fallback logic in one place so the three
 * `createApplicant` callsites in App.tsx can all use the same contract.
 */
export interface NamePair {
  firstName: string;
  lastName: string;
}

export function deriveNamePairFromUser(user: {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
} | null | undefined): NamePair {
  if (user?.firstName && user.firstName.trim() && user?.lastName && user.lastName.trim()) {
    return { firstName: user.firstName.trim(), lastName: user.lastName.trim() };
  }

  const fullName = (user?.fullName ?? '').trim();
  if (fullName) {
    const idx = fullName.lastIndexOf(' ');
    if (idx > 0) {
      const firstName = fullName.slice(0, idx).trim();
      const lastName = fullName.slice(idx + 1).trim();
      if (firstName && lastName) {
        return { firstName, lastName };
      }
    }
    // No whitespace — use the whole string as firstName and sentinel
    // lastName so the create request passes the VTL non-empty check. The
    // user can correct this in their profile afterward.
    return { firstName: fullName, lastName: '(unknown)' };
  }

  return { firstName: '(unknown)', lastName: '(unknown)' };
}

/**
 * Split an arbitrary full-name string into firstName / lastName, used by
 * admin-create flows where the admin types a single "Full Name" field into
 * the legacy create-user dialog.
 */
export function deriveNamePairFromFullName(rawFullName: string): NamePair {
  const fullName = rawFullName.trim();
  if (!fullName) {
    return { firstName: '(unknown)', lastName: '(unknown)' };
  }
  const idx = fullName.lastIndexOf(' ');
  if (idx > 0) {
    const firstName = fullName.slice(0, idx).trim();
    const lastName = fullName.slice(idx + 1).trim();
    if (firstName && lastName) {
      return { firstName, lastName };
    }
  }
  return { firstName: fullName, lastName: '(unknown)' };
}
